import { Bug, LogOut, Menu, Moon, Settings, Sun, User } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { XendrelLogo } from '@/components/brand/XendrelLogo'
import { Button } from '@/components/ui/button'
import { type NavigationRailItem, spaClick } from '@/components/ui/navigation-rail'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useAuth } from '@/contexts/AuthContext'
import { isAdmin } from '@/lib/roles'
import { useTheme } from '@/theme'
import { cn } from '@/utils/cn'
import { BrandHomeLink } from './BrandHomeLink'
import { useCaseRailNav, useFeedbackForm, useGlobalRailNav } from './IconRail'
import { SectionNav } from './SectionSidebar'
import { HOME_ROUTE, type Section, sectionHasSidebar } from './sections'
import { type ActiveNav, useActiveNav } from './useActiveNav'

/** Drawer rows share one shape: 40 px tall, icon + full label. */
const ROW_CLASS =
  "flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5"
const ROW_ACTIVE_CLASS =
  'bg-primary/15 font-medium text-primary hover:bg-primary/15 hover:text-primary'

/**
 * Ref callbacks that scroll the current location into view as the drawer
 * opens: its list can be taller than the screen (docs, a case with lenses).
 */
const reveal = (block: ScrollLogicalPosition) => (el: HTMLElement | null) =>
  el?.scrollIntoView({ block })
const revealRow = reveal('nearest')
const revealSection = reveal('start')

function DrawerBadge({ item }: { item: NavigationRailItem }) {
  if (item.badge == null || item.badge <= 0) return null
  return (
    <span
      title={item.badgeLabel}
      className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground tabular-nums"
    >
      <span aria-hidden={!!item.badgeLabel}>{item.badge > 99 ? '99+' : item.badge}</span>
      {item.badgeLabel && <span className="sr-only">{item.badgeLabel}</span>}
    </span>
  )
}

interface DrawerListProps {
  items: NavigationRailItem[]
  activeId: string | undefined
  onSelect: (id: string) => void
  /** Extra content rendered right under an item (a section's sub-navigation). */
  renderBelow?: (item: NavigationRailItem) => ReactNode
}

/** The rail's items as readable rows: icon, full label, badge; lenses indented. */
function DrawerList({ items, activeId, onSelect, renderBelow }: DrawerListProps) {
  return (
    <div className="flex flex-col gap-0.5">
      {items.map(item => {
        const active = item.id === activeId
        const className = cn(ROW_CLASS, item.nested && 'pl-9', active && ROW_ACTIVE_CLASS)
        const inner = (
          <>
            {item.icon}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <DrawerBadge item={item} />
          </>
        )
        return (
          <div key={item.id} className="contents">
            {item.href ? (
              <a
                ref={active ? revealRow : undefined}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                onClick={e => spaClick(e, () => onSelect(item.id))}
                className={className}
              >
                {inner}
              </a>
            ) : (
              <button
                ref={active ? revealRow : undefined}
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => onSelect(item.id)}
                className={className}
              >
                {inner}
              </button>
            )}
            {renderBelow?.(item)}
            {item.groupEnd && <Separator className="my-1.5" />}
          </div>
        )
      })}
    </div>
  )
}

interface DrawerNavProps {
  nav: ActiveNav
  /** Closes the drawer; also called for the current page's row, which changes no route. */
  onNavigate: () => void
}

/**
 * The brand — the way home, as the rail's corner mark is — and the mode's own
 * header stay put; the rows scroll under them and end with the footer.
 */
function DrawerFrame({
  header,
  children,
  nav,
  onNavigate,
}: DrawerNavProps & { header?: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="flex shrink-0 flex-col gap-1 px-2 pt-2">
        <BrandHomeLink
          onNavigate={onNavigate}
          // The margin keeps clear of the sheet's close button.
          className="mr-10 flex min-h-12 items-center rounded-lg px-3 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XendrelLogo className="h-5 text-foreground" />
        </BrandHomeLink>
        {header}
        <Separator className="mt-1" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
        {children}
        <DrawerFooter nav={nav} onNavigate={onNavigate} />
      </div>
    </>
  )
}

/** Case mode: the case chip (way back to all cases) and every case view. */
function CaseDrawerNav({ nav, onNavigate }: DrawerNavProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, select, caseLabel, caseInitials } = useCaseRailNav(nav.caseId)
  const header = (
    <a
      href={HOME_ROUTE}
      onClick={e => spaClick(e, () => navigate(HOME_ROUTE))}
      className="flex min-h-12 items-center gap-3 rounded-lg px-3 py-1.5 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-sm font-semibold text-primary">
        {caseInitials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-foreground">{caseLabel}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {t('nav.cases.backToCases')}
        </span>
      </span>
    </a>
  )
  return (
    <DrawerFrame header={header} nav={nav} onNavigate={onNavigate}>
      <DrawerList
        items={items}
        activeId={nav.activeSubView?.path}
        onSelect={id => {
          select(id)
          onNavigate()
        }}
      />
    </DrawerFrame>
  )
}

/** A section's sub-navigation, indented under its row with roomier tap targets. */
function DrawerSectionNav({ section, nav, onNavigate }: DrawerNavProps & { section: Section }) {
  return (
    <div className="ml-5 border-l border-border [&_button]:min-h-9">
      <SectionNav
        section={section}
        caseId={undefined}
        activeWorkspace={nav.activeWorkspace}
        activeSubView={nav.activeSubView}
        pathname={nav.pathname}
        onItemSelect={onNavigate}
      />
    </div>
  )
}

/** Global mode: the product sections, each with its sub-navigation underneath. */
function GlobalDrawerNav({ nav, onNavigate }: DrawerNavProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, select, visibleSections } = useGlobalRailNav(s => {
    if (nav.pathname !== s.defaultRoute) navigate(s.defaultRoute)
  })
  const sectionOf = (id: string) => visibleSections.find(s => s.key === id)
  // A section that is not in the rail (the in-app docs) still needs its
  // sub-navigation while one of its pages is open.
  const unlisted =
    sectionHasSidebar(nav.section) && !sectionOf(nav.section.key) ? nav.section : undefined
  return (
    <DrawerFrame nav={nav} onNavigate={onNavigate}>
      <DrawerList
        items={items}
        activeId={nav.section?.key}
        onSelect={id => {
          select(id)
          onNavigate()
        }}
        renderBelow={item => {
          const section = sectionOf(item.id)
          return sectionHasSidebar(section) ? (
            <DrawerSectionNav section={section} nav={nav} onNavigate={onNavigate} />
          ) : null
        }}
      />
      {unlisted && (
        <>
          <Separator className="my-1.5" />
          <div ref={revealSection} className="px-3 py-2 text-sm font-medium text-foreground">
            {t(`nav.sections.${unlisted.key}`)}
          </div>
          <DrawerSectionNav section={unlisted} nav={nav} onNavigate={onNavigate} />
        </>
      )}
    </DrawerFrame>
  )
}

/** The user menu as plain rows: who is signed in, theme toggle, logout. */
function DrawerAccountRows() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  return (
    <>
      <div className="flex min-h-10 items-center gap-3 px-3 text-sm text-foreground">
        <User className="size-5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate font-medium">{user?.username}</span>
        {isAdmin(user?.role) && (
          <span className="shrink-0 rounded bg-warning-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground">
            {user?.role}
          </span>
        )}
      </div>
      <button
        type="button"
        className={ROW_CLASS}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        {theme === 'dark' ? <Sun /> : <Moon />}
        <span className="min-w-0 flex-1 truncate">
          {theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
        </span>
      </button>
      <button
        type="button"
        className={cn(ROW_CLASS, 'text-destructive hover:text-destructive')}
        onClick={() => logout()}
      >
        <LogOut />
        <span className="min-w-0 flex-1 truncate">{t('nav.logout')}</span>
      </button>
    </>
  )
}

/** Case settings and feedback, then the user menu rows. */
function DrawerFooter({ nav, onNavigate }: DrawerNavProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const openFeedback = useFeedbackForm()
  const settingsHref = nav.caseId ? `/cases/${nav.caseId}/settings` : undefined
  const settingsActive = settingsHref != null && nav.pathname.startsWith(settingsHref)
  return (
    <div className="mt-auto flex flex-col gap-0.5 pt-2">
      <Separator className="mb-1.5" />
      {settingsHref && (
        <a
          ref={settingsActive ? revealRow : undefined}
          href={settingsHref}
          aria-current={settingsActive ? 'page' : undefined}
          onClick={e =>
            spaClick(e, () => {
              navigate(settingsHref)
              onNavigate()
            })
          }
          className={cn(ROW_CLASS, settingsActive && ROW_ACTIVE_CLASS)}
        >
          <Settings />
          <span className="min-w-0 flex-1 truncate">{t('nav.cases.caseSettings')}</span>
        </a>
      )}
      {openFeedback && (
        <button
          type="button"
          className={ROW_CLASS}
          onClick={() => {
            onNavigate()
            void openFeedback()
          }}
        >
          <Bug />
          <span className="min-w-0 flex-1 truncate">{t('nav.feedback')}</span>
        </button>
      )}
      <DrawerAccountRows />
    </div>
  )
}

/**
 * Phone navigation (below md): a menu button for the top bar that opens a left
 * drawer with everything the IconRail and the SectionSidebar offer on desktop
 * — same data hooks, laid out as rows of icon + full label.
 *
 * The drawer is tied to the pathname it was opened on, so any route change
 * closes it; its content mounts only while it is open.
 */
export function MobileNav() {
  const { t } = useTranslation()
  const nav = useActiveNav()
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const close = () => setOpenedAt(null)
  return (
    <Sheet
      open={openedAt === nav.pathname}
      onOpenChange={open => setOpenedAt(open ? nav.pathname : null)}
    >
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-9 shrink-0 md:hidden"
          aria-label={t('nav.openNavigation')}
        >
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        aria-describedby={undefined}
        className="gap-0 bg-sidebar p-0 data-[side=left]:w-[min(20rem,86vw)]"
      >
        <SheetTitle className="sr-only">{t('nav.navigation')}</SheetTitle>
        <nav aria-label={t('nav.navigation')} className="flex min-h-0 flex-1 flex-col">
          {nav.caseId ? (
            <CaseDrawerNav nav={nav} onNavigate={close} />
          ) : (
            <GlobalDrawerNav nav={nav} onNavigate={close} />
          )}
        </nav>
      </SheetContent>
    </Sheet>
  )
}
